<template>
  <section class="data-faq-page container">
    <div class="faq-shell">
      <div class="faq-hero">
        <p class="faq-kicker">Data Inventory Support</p>
        <h1 class="faq-title">Frequently asked questions about data inventory</h1>
        <p class="faq-subtitle">
          Everything you need to know about dataset submission, microdata access, search, and
          support on the platform.
        </p>
      </div>

      <div class="faq-panel">
        <div class="accordion faq-accordion" role="tablist">
          <b-card v-for="item in faqItems" :key="item.id" no-body class="faq-item">
            <b-card-header header-tag="header" class="faq-item__header" role="tab">
              <b-button block v-b-toggle="item.id" class="faq-item__trigger text-left">
                <span class="faq-item__icon">
                  <i class="fas fa-plus"></i>
                </span>
                <span>{{ item.question }}</span>
              </b-button>
            </b-card-header>

            <b-collapse :id="item.id" accordion="data-faq-accordion" role="tabpanel">
              <b-card-body class="faq-item__body">
                <b-card-text>{{ item.answer }}</b-card-text>
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'DataInventoryFaq',
  data() {
    return {
      faqItems: [
        {
          id: 'accordion-1',
          question: 'How do I upload datasets?',
          answer:
            'To upload datasets, click the submit datasets tab on the homepage and fill the form provided.',
        },
        {
          id: 'accordion-2',
          question: 'How many submissions am I allowed to make?',
          answer:
            'You can make as many submissions as needed, as long as they cover previous and ongoing health-related data collection activities carried out by your organization since 2015.',
        },
        {
          id: 'accordion-3',
          question: 'Who can I contact if I need help?',
          answer: 'Our contact information is available on the About page of this website.',
        },
        {
          id: 'accordion-4',
          question: 'How can I request for data (microdata)?',
          answer:
            'To download microdata, go to the get microdata tab on the survey or data collection activity of your choice, register or log in, and complete the request form appropriately. An email will be sent to authorize access before the data can be downloaded.',
        },
        {
          id: 'accordion-5',
          question: "I can't find the datasets I'm looking for. What do I do?",
          answer:
            'Use the search icon at the top of the page to type the name of the dataset you need. You can also use the available filters to narrow your search. If you still cannot find it, the dataset may not have been published yet. This inventory is updated periodically, so please check back regularly.',
        },
        {
          id: 'accordion-6',
          question: 'How do I know if my submission has been accepted?',
          answer:
            'Generally, it takes about one month for submitted datasets to be verified and published. Check the website regularly to confirm whether your submission has been published.',
        },
        {
          id: 'accordion-7',
          question:
            "I know of some data collection activities by other organizations, and I can't find them here.",
          answer:
            'If you know of data that is not available on this website, please let us know the name of the organization and the activity on our feedback page.',
        },
        {
          id: 'accordion-8',
          question: 'How do I contact an organization that submitted a dataset?',
          answer:
            'The contact details of the implementing organization are available for each dataset. Select the project of interest and click the contact details tab.',
        },
        {
          id: 'accordion-9',
          question: "What if my question is not addressed in the FAQ's?",
          answer: 'Please email us and we will get back to you shortly.',
        },
      ],
    };
  },
};
</script>

<style scoped>
.data-faq-page {
  padding-top: clamp(4.5rem, 6vw, 5.75rem);
  padding-bottom: 4rem;
}

.faq-shell {
  position: relative;
  padding: clamp(1.4rem, 3vw, 2.4rem);
  border-radius: 32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(244, 249, 245, 0.95));
  border: 1px solid rgba(28, 69, 44, 0.08);
  box-shadow: 0 24px 60px rgba(17, 41, 24, 0.08);
  overflow: hidden;
}

.faq-shell::before {
  content: '';
  position: absolute;
  top: -3rem;
  right: -2rem;
  width: 18rem;
  height: 18rem;
  background: radial-gradient(circle, rgba(48, 171, 76, 0.14), transparent 70%);
  pointer-events: none;
}

.faq-hero,
.faq-panel {
  position: relative;
  z-index: 1;
}

.faq-hero {
  max-width: 50rem;
  margin-bottom: 1.75rem;
}

.faq-kicker {
  margin: 0 0 0.55rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #2a8a43;
}

.faq-title {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.15rem);
  line-height: 1.08;
  font-weight: 700;
  color: #153322;
}

.faq-subtitle {
  margin: 1rem 0 0;
  max-width: 44rem;
  font-size: 1rem;
  line-height: 1.8;
  color: #51685b;
}

.faq-panel {
  padding: 0.35rem;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(28, 69, 44, 0.06);
}

.faq-accordion {
  display: grid;
  gap: 0.9rem;
  font-family: 'Roboto', sans-serif;
}

.faq-item {
  overflow: hidden;
  border: 1px solid rgba(28, 69, 44, 0.08) !important;
  border-radius: 24px !important;
  background: rgba(255, 255, 255, 0.88) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.faq-item__header {
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
}

.faq-item__trigger {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-height: 4.5rem;
  padding: 1.1rem 1.2rem !important;
  border: 0 !important;
  background: transparent !important;
  color: #173523 !important;
  font-size: 1rem;
  font-weight: 700;
  box-shadow: none !important;
}

.faq-item__trigger:hover,
.faq-item__trigger:focus {
  text-decoration: none;
}

.faq-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 999px;
  background: rgba(47, 159, 73, 0.1);
  color: #1f6f33;
}

.faq-item__body {
  padding: 0 1.2rem 1.2rem !important;
}

.faq-item__body .card-text {
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.75;
  color: #4f6759;
}

@media (max-width: 991px) {
  .data-faq-page {
    padding-top: 4.5rem;
  }
}

@media (max-width: 767px) {
  .data-faq-page {
    padding-top: 4rem;
    padding-bottom: 2.5rem;
  }

  .faq-shell {
    padding: 1.2rem;
    border-radius: 24px;
  }

  .faq-hero {
    margin-bottom: 1.25rem;
  }

  .faq-title {
    font-size: 1.7rem;
    line-height: 1.12;
  }

  .faq-kicker {
    font-size: 0.7rem;
    letter-spacing: 0.14em;
  }

  .faq-subtitle {
    font-size: 0.95rem;
    line-height: 1.7;
  }

  .faq-item {
    border-radius: 20px !important;
  }

  .faq-item__trigger {
    min-height: 4rem;
    padding: 1rem !important;
    font-size: 0.95rem;
  }

  .faq-item__body {
    padding: 0 1rem 1rem !important;
  }

  .faq-item__body .card-text {
    font-size: 0.92rem;
    line-height: 1.65;
  }
}

@media (max-width: 575px) {
  .data-faq-page {
    padding-top: 3.5rem;
    padding-bottom: 2rem;
  }

  .faq-shell {
    padding: 1rem;
    border-radius: 20px;
  }

  .faq-title {
    font-size: 1.45rem;
  }

  .faq-subtitle {
    font-size: 0.9rem;
    margin-top: 0.85rem;
  }

  .faq-panel {
    padding: 0;
    background: transparent;
    border: 0;
  }

  .faq-accordion {
    gap: 0.75rem;
  }

  .faq-item {
    border-radius: 18px !important;
  }

  .faq-item__trigger {
    gap: 0.75rem;
    padding: 0.95rem !important;
    font-size: 0.9rem;
  }

  .faq-item__icon {
    width: 1.8rem;
    height: 1.8rem;
  }

  .faq-item__body {
    padding: 0 0.95rem 0.95rem !important;
  }
}
</style>
